import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AppBar,
  Toolbar,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Container,
  TablePagination,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  ContactMail as ContactMailIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Folder as FolderIcon,
  TrendingUp as TrendingUpIcon,
  CalendarMonth as CalendarIcon,
  AdminPanelSettings as AdminIcon,
  AutoAwesome as QuoteAssistIcon,
  FactCheck as QuoteReviewIcon,
} from '@mui/icons-material';
import adminApi, { clearAdminAuth } from '../services/adminApi';

// ─── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: '100%',
      bgcolor: 'background.paper',
    }}
  >
    <Box
      sx={{
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        borderRadius: 1.5,
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 24, color: 'primary.main' } })}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: 0.3 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

// ─── Table header cell ───────────────────────────────────────────────────────
const HeadCell = ({ children, ...props }) => (
  <TableCell
    {...props}
    sx={{
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: 'text.secondary',
      borderBottom: '2px solid',
      borderColor: 'divider',
      py: 1.5,
      bgcolor: 'background.paper',
      ...props.sx,
    }}
  >
    {children}
  </TableCell>
);

// ─── Numeric badge ───────────────────────────────────────────────────────────
const NumBadge = ({ value }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 32,
      px: 1,
      py: 0.25,
      borderRadius: 1,
      bgcolor: (theme) => value > 0 ? alpha(theme.palette.primary.main, 0.08) : 'action.hover',
      color: value > 0 ? 'primary.main' : 'text.disabled',
      fontWeight: 600,
      fontSize: '0.8125rem',
    }}
  >
    {value}
  </Box>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  // Contact forms state
  const [contactForms, setContactForms] = useState([]);
  const [contactTotal, setContactTotal] = useState(0);
  const [contactPage, setContactPage] = useState(0);
  const [contactRowsPerPage, setContactRowsPerPage] = useState(25);

  // Tenants state
  const [tenants, setTenants] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── data fetching ──
  const fetchContactForms = useCallback(async (page, rowsPerPage) => {
    try {
      const res = await adminApi.getContactForms(rowsPerPage, page * rowsPerPage);
      setContactForms(res.data.items);
      setContactTotal(res.data.total);
    } catch (err) {
      console.error(err);
      setError('Failed to load contact form submissions');
    }
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await adminApi.getTenantsOverview();
      setTenants(res.data.tenants);
    } catch (err) {
      console.error(err);
      setError('Failed to load tenant data');
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchContactForms(contactPage, contactRowsPerPage),
        fetchTenants(),
      ]);
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchContactForms(contactPage, contactRowsPerPage);
  }, [contactPage, contactRowsPerPage, fetchContactForms]);

  const handleLogout = () => {
    clearAdminAuth();
    onLogout();
    navigate('/admin');
  };

  // ── aggregate stats ──
  const totalTenants = tenants.length;
  const totalUsers = tenants.reduce((s, t) => s + (t.user_count || 0), 0);
  const totalProjects = tenants.reduce((s, t) => s + (t.project_count || 0), 0);
  const projectsLastWeek = tenants.reduce((s, t) => s + (t.projects_last_week || 0), 0);
  const projectsLastMonth = tenants.reduce((s, t) => s + (t.projects_last_month || 0), 0);
  const quoteAssistProjects = tenants.reduce((s, t) => s + (t.quote_assist_projects || 0), 0);
  const quoteAssistProjectsLastWeek = tenants.reduce((s, t) => s + (t.quote_assist_projects_last_week || 0), 0);
  const quoteAssistProjectsLastMonth = tenants.reduce((s, t) => s + (t.quote_assist_projects_last_month || 0), 0);
  const quoteReviewProjects = tenants.reduce((s, t) => s + (t.quote_review_projects || 0), 0);
  const quoteReviewProjectsLastWeek = tenants.reduce((s, t) => s + (t.quote_review_projects_last_week || 0), 0);
  const quoteReviewProjectsLastMonth = tenants.reduce((s, t) => s + (t.quote_review_projects_last_month || 0), 0);

  // ── render ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 3 } }}>
          <AdminIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1.1rem' }}>
            Admin Dashboard
          </Typography>
          <Button
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{ textTransform: 'none', fontWeight: 500, color: 'text.secondary' }}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: (theme) => alpha(theme.palette.error.main, 0.1), border: '1px solid', borderColor: 'error.main', borderRadius: 2 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Paper>
        )}

        {/* Summary cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 2,
            mb: 4,
          }}
        >
          <StatCard icon={<BusinessIcon />} label="Tenants" value={totalTenants} />
          <StatCard icon={<PeopleIcon />} label="Total Users" value={totalUsers} />
          <StatCard icon={<FolderIcon />} label="Total Projects" value={totalProjects} />
          <StatCard icon={<TrendingUpIcon />} label="Projects (7d)" value={projectsLastWeek} />
          <StatCard icon={<CalendarIcon />} label="Projects (30d)" value={projectsLastMonth} />
          <StatCard icon={<QuoteAssistIcon />} label="Quote Assist (All)" value={quoteAssistProjects} />
          <StatCard icon={<QuoteAssistIcon />} label="Quote Assist (7d)" value={quoteAssistProjectsLastWeek} />
          <StatCard icon={<QuoteAssistIcon />} label="Quote Assist (30d)" value={quoteAssistProjectsLastMonth} />
          <StatCard icon={<QuoteReviewIcon />} label="Quote Review (All)" value={quoteReviewProjects} />
          <StatCard icon={<QuoteReviewIcon />} label="Quote Review (7d)" value={quoteReviewProjectsLastWeek} />
          <StatCard icon={<QuoteReviewIcon />} label="Quote Review (30d)" value={quoteReviewProjectsLastMonth} />
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                minHeight: 48,
              },
            }}
          >
            <Tab icon={<ContactMailIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Contact Submissions" />
            <Tab icon={<BusinessIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Tenants" />
          </Tabs>
        </Box>

        {/* ─── Contact Forms Tab ─── */}
        {tab === 0 && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', bgcolor: 'background.paper' }}>
            <TableContainer>
              <Table size="small" sx={{ bgcolor: 'background.paper' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.paper' }}>
                    <HeadCell width={60}>ID</HeadCell>
                    <HeadCell width={220}>Email</HeadCell>
                    <HeadCell>Enquiry</HeadCell>
                    <HeadCell width={160}>Submitted</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: 'background.paper' }}>
                  {contactForms.length === 0 ? (
                    <TableRow sx={{ bgcolor: 'background.paper' }}>
                      <TableCell colSpan={4} align="center" sx={{ py: 6, bgcolor: 'background.paper' }}>
                        <Typography color="text.secondary" variant="body2">No submissions yet</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contactForms.map((cf) => (
                      <TableRow key={cf.id} hover sx={{ '&:last-child td': { borderBottom: 0 }, bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell sx={{ color: 'text.primary', fontSize: '0.8125rem', bgcolor: 'background.paper' }}>{cf.id}</TableCell>
                        <TableCell sx={{ fontWeight: 500, fontSize: '0.8125rem', color: 'text.primary', bgcolor: 'background.paper' }}>{cf.email}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', maxWidth: 480, whiteSpace: 'pre-wrap', color: 'text.primary', bgcolor: 'background.paper' }}>
                          {cf.enquiry}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary', bgcolor: 'background.paper' }}>
                          {cf.created_ts
                            ? new Date(cf.created_ts).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={contactTotal}
              page={contactPage}
              onPageChange={(_, p) => setContactPage(p)}
              rowsPerPage={contactRowsPerPage}
              onRowsPerPageChange={(e) => {
                setContactRowsPerPage(parseInt(e.target.value, 10));
                setContactPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          </Paper>
        )}

        {/* ─── Tenants Tab ─── */}
        {tab === 1 && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', bgcolor: 'background.paper' }}>
            <TableContainer>
              <Table size="small" sx={{ bgcolor: 'background.paper', minWidth: 2120 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.paper' }}>
                    <HeadCell>Tenant</HeadCell>
                    <HeadCell width={130}>Registered</HeadCell>
                    <HeadCell width={90} align="center">Users</HeadCell>
                    <HeadCell width={100} align="center">
                      <Tooltip title="Users who logged in during the last 30 days" arrow>
                        <span>MAU</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={110} align="center">Projects</HeadCell>
                    <HeadCell width={100} align="center">
                      <Tooltip title="Projects created in the last 7 days" arrow>
                        <span>Last 7d</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={100} align="center">
                      <Tooltip title="Projects created in the last 30 days" arrow>
                        <span>Last 30d</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={110} align="center">
                      <Tooltip title="RAMS generated in the last 30 days" arrow>
                        <span>RAMS 30d</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={130} align="center">
                      <Tooltip title="Total AI assistant conversations" arrow>
                        <span>AI Conv (All)</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={130} align="center">
                      <Tooltip title="AI assistant conversations in the last 7 days" arrow>
                        <span>AI Conv 7d</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={130} align="center">
                      <Tooltip title="AI assistant conversations in the last 30 days" arrow>
                        <span>AI Conv 30d</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={120} align="center">
                      <Tooltip title="Projects where Quote Assist successfully generated quote items" arrow>
                        <span>Q Assist (All)</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={120} align="center">
                      <Tooltip title="Projects where Quote Assist ran in the last 7 days" arrow>
                        <span>Q Assist 7d</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={120} align="center">
                      <Tooltip title="Projects where Quote Assist ran in the last 30 days" arrow>
                        <span>Q Assist 30d</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={120} align="center">
                      <Tooltip title="Projects where AI Quote Review successfully ran" arrow>
                        <span>Q Review (All)</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={120} align="center">
                      <Tooltip title="Projects where AI Quote Review ran in the last 7 days" arrow>
                        <span>Q Review 7d</span>
                      </Tooltip>
                    </HeadCell>
                    <HeadCell width={120} align="center">
                      <Tooltip title="Projects where AI Quote Review ran in the last 30 days" arrow>
                        <span>Q Review 30d</span>
                      </Tooltip>
                    </HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: 'background.paper' }}>
                  {tenants.length === 0 ? (
                    <TableRow sx={{ bgcolor: 'background.paper' }}>
                      <TableCell colSpan={17} align="center" sx={{ py: 6, bgcolor: 'background.paper' }}>
                        <Typography color="text.secondary" variant="body2">No tenants found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tenants.map((t) => (
                      <TableRow key={t.tenant_id} hover sx={{ '&:last-child td': { borderBottom: 0 }, bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.primary', bgcolor: 'background.paper' }}>{t.name}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary', bgcolor: 'background.paper' }}>
                          {t.created_date
                            ? new Date(t.created_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.user_count} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.monthly_active_users} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.project_count} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.projects_last_week} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.projects_last_month} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.ram_generations_last_month} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.assistant_conversations_total} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.assistant_conversations_last_week} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.assistant_conversations_last_month} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.quote_assist_projects} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.quote_assist_projects_last_week} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.quote_assist_projects_last_month} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.quote_review_projects} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.quote_review_projects_last_week} /></TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'background.paper' }}><NumBadge value={t.quote_review_projects_last_month} /></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default AdminDashboard;
